<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useDebounceFn } from '@vueuse/core'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import CountryFlag from '@/shared/components/CountryFlag/CountryFlag.vue'
  import BaseMultiSelect from '@/shared/components/BaseMultiSelect/BaseMultiSelect.vue'
  import { useUserStore } from '@/shared/store'
  import { type Country, CountryBoundType } from '@/shared/types/api.types'
  import {
    useValidation,
    useValidators,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import {
    DEFAULT_TITLE_KEY,
    DEFAULT_VALUE_KEY,
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
  } from './FlowDestinationWidget.constants'
  import type { Locale } from '@/shared/types/common.types'

  // TODO: Move FlowView event signatures upper in scope?
  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const { t, locale } = useI18n()
  const { destinationValidator } = useValidators()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const gaService = useGoogleAnalytics()
  const selectedOption = ref((() => userStore.destination.map((country) => country?.id))())
  const listOfDestionations = ref<Country[]>([])
  const options = ref<Country[]>([])

  // TODO: Add requests cancelation on component unmount

  const validationRules = {
    selectedOption: {
      destinationValidator,
    },
  }

  const { v$, errors } = useValidation(validationRules, { selectedOption })

  const onProceed = () => {
    if (options.value.length === 0) return

    v$.value.$touch()

    if (!v$.value.$error) {
      const formattedDestinations = selectedOption.value.map((id) => {
        const currentOption = options.value.find((option) => option.id === id)
        return currentOption
          ? {
              id: currentOption.id,
              name: currentOption.name,
            }
          : undefined
      })

      // @ts-ignore
      userStore.setDestination(formattedDestinations, true)
      gaService.sendDestinationProceed(formattedDestinations.map((d) => d?.id || ''))
      emit(PROCEED_EVENT_NAME)
    }
  }

  onMounted(async () => {
    await emit(LOAD_EVENT_NAME, async () => {
      listOfDestionations.value = await userStore.fetchCountries(
        CountryBoundType.OUTBUND,
        locale.value as Locale
      )
      options.value = listOfDestionations.value
      onPerformanceCheckEnd()
    })
  })

  const initFindCountries = async () => {
    options.value = listOfDestionations.value
  }

  const findCountries = useDebounceFn(async (search: string): Promise<Country[]> => {
    try {
      if (search) {
        const filteredIds = listOfDestionations.value
          .filter((d) => d.searchString.toLowerCase().includes(search.toLowerCase()))
          .map((c) => c.code)

        const res = listOfDestionations.value.map((option) => ({
          ...option,
          hidden: !filteredIds.includes(option.id),
        }))

        return res
      } else {
        return listOfDestionations.value
      }
    } catch {
      return listOfDestionations.value
    }
  }, 500)

  const onInput = async (search: string) => {
    const newOptions = await findCountries(search)
    options.value = newOptions ?? listOfDestionations.value
  }

  const resetOptionValues = () => {
    options.value = listOfDestionations.value
  }

  const filter = (option: any) => !option.hidden

  watch(
    () => selectedOption.value,
    () => userStore.setTravelDates(null, null, true)
  )
</script>

<template>
  <FlowCard :title="t('outbound_destinations_header')" with-button @button-click="onProceed">
    <div class="flow-destination-content">
      <h2 class="flow-destination-subtitle">
        {{ $t('outbound_destinations_subtitle') }}
      </h2>
      <BaseMultiSelect
        v-model="selectedOption"
        :label="t('home_form_outbound_destinations_label')"
        filterable
        :title-key="DEFAULT_TITLE_KEY"
        :value-key="DEFAULT_VALUE_KEY"
        :options="options"
        :errors="errors.selectedOption"
        :disabled="!listOfDestionations.length"
        :filter-function="filter"
        @input="onInput"
        @change="initFindCountries"
        @close="resetOptionValues"
        @clear="resetOptionValues"
        @open="resetOptionValues"
      >
        <template #option="option">
          <div class="flow-destination-option">
            <CountryFlag :iso="option.isocode" class="option-flag" />
            <span class="option-title">{{ option.name }}</span>
          </div>
        </template>
      </BaseMultiSelect>
    </div>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowDestinationWidget';
</style>
